import { useState, useEffect, useRef } from "react";
import { keyframes } from "@emotion/react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, TextField, IconButton, Typography, Paper, Avatar,
  CircularProgress, Button, Tooltip, Chip } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import PaymentIcon from "@mui/icons-material/Payment";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import CallEndIcon from "@mui/icons-material/CallEnd";

const token = () => localStorage.getItem("biyz_token");
const authHead = () => ({ Authorization: `Bearer ${token()}` });

// Oyunlastirma: chat cevabindaki seri/XP verisini Layout rozetine ilet (canli guncelleme)
const emitGami = (data) => {
  if (data && data.gamification) {
    try { window.dispatchEvent(new CustomEvent("bi-gami", { detail: data.gamification })); } catch {}
  }
};

// VAD amplitude thresholds
const VAD_SPEECH_RMS    = 0.018;  // above this → speech started
const VAD_SILENCE_RMS   = 0.010;  // below this → potential silence
const VAD_SILENCE_MS    = 1500;   // ms of silence to confirm speech end
const VAD_MIN_SPEECH_MS = 400;    // ignore clips shorter than this

const breatheKf = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 6px 24px rgba(33,150,243,0.35); }
  50% { transform: scale(1.045); box-shadow: 0 9px 30px rgba(33,150,243,0.5); }
`;
const recordPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.5); }
  50% { box-shadow: 0 0 0 8px rgba(239,68,68,0); }
`;
const livePulseGreen = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.6); }
  50% { box-shadow: 0 0 0 8px rgba(34,197,94,0); }
`;
const waveBarKf = keyframes`
  0%, 100% { transform: scaleY(0.28); }
  50% { transform: scaleY(1); }
`;

function ttsStreamUrl(audioKey, fallbackUrl) {
  return audioKey ? `/api/platform/chat/audio-stream/${audioKey}` : fallbackUrl;
}

// iOS Safari webm/ogg DESTEKLEMEZ — mp4 eklenmezse mobilde kayit bos kalir (bot dinler ama cevap yok)
function pickAudioMime() {
  const cands = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mp4;codecs=mp4a.40.2", "audio/ogg"];
  for (const c of cands) {
    try { if (window.MediaRecorder && MediaRecorder.isTypeSupported(c)) return c; } catch {}
  }
  return ""; // tarayici varsayilanini kullan
}
function mimeExt(mime) {
  if (!mime) return ".webm";
  if (mime.includes("mp4")) return ".mp4";
  if (mime.includes("ogg")) return ".ogg";
  return ".webm";
}

function detectLang(text) {
  if (!text) return null;
  if (/[؀-ۿ]/.test(text)) return "Arabic";
  if (/[Ѐ-ӿ]/.test(text)) return "Russian";
  if (/[äöüÄÖÜß]/.test(text)) return "German";
  if (/[ğüşıöçĞÜŞİÖÇ]/.test(text)) return "Turkish";
  if (/[àâçèéêëîïôùûüÀÂÇÈÉÊËÎÏÔÙÛÜ]/.test(text)) return "French";
  if (/[áéíóúñÁÉÍÓÚÑ]/.test(text)) return "Spanish";
  return null;
}

function BotAvatar({ bot, size = 36, sx = {} }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [brandFailed, setBrandFailed] = useState(false);
  if (!imgFailed && bot?.avatar) {
    return (
      <Avatar sx={{ width: size, height: size, p: 0, overflow: "hidden",
        background: "transparent", border: "2px solid rgba(255,255,255,0.5)", ...sx }}>
        <img src={bot.avatar} alt={bot.name || "bot"}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          onError={() => setImgFailed(true)} />
      </Avatar>
    );
  }
  if (!brandFailed) {
    return (
      <Avatar sx={{ width: size, height: size, p: 0, overflow: "hidden",
        background: "transparent", border: "2px solid rgba(255,255,255,0.5)", ...sx }}>
        <img src="/biyz-brand-logo.png" alt="biyapayzeka"
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
          onError={() => setBrandFailed(true)} />
      </Avatar>
    );
  }
  return (
    <Avatar sx={{ width: size, height: size,
      background: "linear-gradient(135deg,#6c3de0,#2196f3,#00bcd4)",
      border: "2px solid rgba(255,255,255,0.5)", ...sx }}>
      <SmartToyIcon sx={{ color: "white", fontSize: size * 0.5 }} />
    </Avatar>
  );
}

export default function ChatPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};

  const [bots, setBots] = useState([]);
  const [myBotIds, setMyBotIds] = useState(null);
  const [selectedBotId, setSelectedBotId] = useState(state.botId || "");
  const [selectedBot, setSelectedBot] = useState(null);
  const [chatCode, setChatCode] = useState(state.chatCode || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [ttsOn, setTtsOn] = useState(false);
  const [recording, setRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [webSearch, setWebSearch] = useState(false);
  const [status, setStatus] = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]);
  // Live conversation
  const [liveMode, setLiveMode] = useState(false);
  const [liveState, setLiveState] = useState("idle"); // idle|listening|recording|processing|playing
  // Egitmenin konusma hizi (TTS playbackRate): yavas/normal/hizli
  const [speechSpeed, setSpeechSpeed] = useState(1.0);
  const speechSpeedRef = useRef(1.0);
  useEffect(() => { speechSpeedRef.current = speechSpeed; }, [speechSpeed]);

  // General refs
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);
  // Manual recording
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  // Audio visualizer (orb)
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const audioFreqDataRef = useRef(null);
  const rafRef = useRef(null);
  const orbRef = useRef(null);
  const orbGlowRef = useRef(null);
  const orbRing1Ref = useRef(null);
  const orbRing2Ref = useRef(null);
  // Live conversation (VAD)
  const liveModeRef = useRef(false);
  const liveStateRef = useRef("idle");
  const vadStreamRef = useRef(null);
  const vadAudioCtxRef = useRef(null);
  const vadAnalyserRef = useRef(null);
  const vadMRRef = useRef(null);
  const vadChunksRef = useRef([]);
  const vadRafRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const speechStartRef = useRef(null);

  // ── Orb visualizer ──────────────────────────────────────────────
  const applyAudioLevel = (level) => {
    const l = Math.max(0, Math.min(1, level));
    if (orbRef.current) orbRef.current.style.transform = `scale(${1 + l * 0.34})`;
    if (orbGlowRef.current) orbGlowRef.current.style.opacity = String(0.26 + l * 0.5);
    if (orbRing1Ref.current) {
      orbRing1Ref.current.style.transform = `scale(${1 + l * 0.55})`;
      orbRing1Ref.current.style.opacity = String(Math.max(0, 0.5 - l * 0.18));
    }
    if (orbRing2Ref.current) {
      orbRing2Ref.current.style.transform = `scale(${1 + l * 0.95})`;
      orbRing2Ref.current.style.opacity = String(Math.max(0, 0.32 - l * 0.16));
    }
  };

  const stopVisualizer = () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    applyAudioLevel(0);
  };

  const startVisualizer = () => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    if (!audioFreqDataRef.current || audioFreqDataRef.current.length !== analyser.frequencyBinCount) {
      audioFreqDataRef.current = new Uint8Array(analyser.frequencyBinCount);
    }
    const data = audioFreqDataRef.current;
    const tick = () => {
      analyser.getByteFrequencyData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i];
      applyAudioLevel((sum / data.length / 255) * 1.7);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const setupAnalyser = (audioEl) => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      if (!analyserRef.current) {
        analyserRef.current = ctx.createAnalyser();
        analyserRef.current.fftSize = 256;
        analyserRef.current.smoothingTimeConstant = 0.78;
      }
      const source = ctx.createMediaElementSource(audioEl);
      source.connect(analyserRef.current);
      analyserRef.current.connect(ctx.destination);
      sourceNodeRef.current = source;
    } catch (e) { console.warn("Ses analiz hatası:", e.message); }
  };

  // ── Effects ─────────────────────────────────────────────────────
  useEffect(() => { fetchBots(); }, []);

  useEffect(() => {
    return () => {
      stopVisualizer();
      if (audioRef.current) { try { audioRef.current.pause(); } catch {} }
      if (sourceNodeRef.current) { try { sourceNodeRef.current.disconnect(); } catch {} }
      if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch {} }
      if (mediaRecorderRef.current?.state === "recording") { try { mediaRecorderRef.current.stop(); } catch {} }
      // Live mode full cleanup
      liveModeRef.current = false;
      if (vadRafRef.current) cancelAnimationFrame(vadRafRef.current);
      if (vadMRRef.current?.state === "recording") { try { vadMRRef.current.stop(); } catch {} }
      if (vadStreamRef.current) vadStreamRef.current.getTracks().forEach(t => t.stop());
      if (vadAudioCtxRef.current) { try { vadAudioCtxRef.current.close(); } catch {} }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  useEffect(() => {
    if (selectedBotId && bots.length > 0) {
      const bot = bots.find(b => String(b._id) === String(selectedBotId));
      setSelectedBot(bot || null);
      if (!chatCode && !state.chatCode) startSession(selectedBotId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBotId, bots]);

  useEffect(() => {
    if (state.chatCode) loadSession(state.chatCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.chatCode]);

  // ── Data fetching ────────────────────────────────────────────────
  const fetchBots = async () => {
    try {
      const [allRes, subRes] = await Promise.all([
        fetch("/api/platform/bots", { headers: authHead() }),
        fetch("/api/subscriptions/me", { headers: authHead() }),
      ]);
      const all = await allRes.json();
      const subs = await subRes.json();
      const subItems = Array.isArray(subs?.subscriptions)
        ? subs.subscriptions.filter(s => s?.isActive)
        : (Array.isArray(subs) ? subs.filter(s => s?.isActive) : []);
      const hasGlobal = subItems.some(s => !s.botIds || s.botIds.length === 0);
      const specificBotIds = subItems.flatMap(s => (s.botIds || []).map(id => String(id)));
      const botList = (Array.isArray(all) ? all : []).sort((a, b) => (a.order || 0) - (b.order || 0));
      setBots(botList);
      setMyBotIds(hasGlobal ? "all" : specificBotIds);
      if (!selectedBotId) {
        if (hasGlobal && botList.length > 0) setSelectedBotId(String(botList[0]._id));
        else if (specificBotIds.length > 0) setSelectedBotId(specificBotIds[0]);
        else if (botList.length > 0) setSelectedBotId(String(botList[0]._id));
      }
    } catch {}
  };

  const hasBotAccess = (botId) => {
    if (myBotIds === null) return true;
    if (myBotIds === "all") return true;
    return myBotIds.includes(String(botId));
  };

  const startSession = async (botId) => {
    if (!botId) return;
    if (!hasBotAccess(botId)) { setStatus("Bu bot için uygun aboneliğiniz yok."); return; }
    try {
      setStatus("Bağlanıyor...");
      const res = await fetch("/api/platform/chat/start", {
        method: "POST",
        headers: { ...authHead(), "Content-Type": "application/json" },
        body: JSON.stringify({ botId }),
      });
      const data = await res.json();
      if (data.ok === false) { setStatus(data.message || "Erişim reddedildi."); return; }
      const code = data.chatCode || data._id;
      if (code) {
        setChatCode(code);
        if (Array.isArray(data.messages)) setMessages(data.messages);
        setStatus("");
      } else {
        setStatus(data.message || "Başlatılamadı");
      }
    } catch { setStatus("Bağlantı hatası"); }
  };

  const loadSession = async (code) => {
    if (!code) return;
    try {
      const res = await fetch(`/api/platform/chat/${code}`, { headers: authHead() });
      const data = await res.json();
      if (data.messages) setMessages(data.messages);
      if (data.bot?._id) setSelectedBotId(String(data.bot._id));
    } catch {}
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setAttachedFiles(prev => [...prev, ...files].slice(0, 5));
    e.target.value = "";
  };
  const removeFile = (idx) => setAttachedFiles(prev => prev.filter((_, i) => i !== idx));

  // ── TTS audio ────────────────────────────────────────────────────
  const playTtsAudio = (audioKey, audioUrl, onPlaybackEnd = null) => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch {} audioRef.current = null; }
    stopVisualizer();
    if (sourceNodeRef.current) { try { sourceNodeRef.current.disconnect(); } catch {} sourceNodeRef.current = null; }
    analyserRef.current = null;
    const audioEl = new Audio(ttsStreamUrl(audioKey, audioUrl));
    audioRef.current = audioEl;
    // Egitmenin konusma hizi — pitch korunarak (yavas/normal/hizli)
    const applySpeed = () => {
      try {
        const r = speechSpeedRef.current || 1.0;
        audioEl.preservesPitch = true;
        audioEl.mozPreservesPitch = true;
        audioEl.webkitPreservesPitch = true;
        audioEl.playbackRate = r;
      } catch {}
    };
    applySpeed();
    setupAnalyser(audioEl);
    audioEl.addEventListener("loadedmetadata", applySpeed);
    audioEl.addEventListener("playing", () => { applySpeed(); setIsSpeaking(true); startVisualizer(); });
    audioEl.addEventListener("pause",   () => { setIsSpeaking(false); stopVisualizer(); });
    audioEl.addEventListener("ended",   () => { setIsSpeaking(false); stopVisualizer(); if (onPlaybackEnd) onPlaybackEnd(); });
    audioEl.addEventListener("error",   () => { setIsSpeaking(false); stopVisualizer(); if (onPlaybackEnd) onPlaybackEnd(); });
    audioEl.play().catch(() => { if (onPlaybackEnd) onPlaybackEnd(); });
  };

  // ── Text message ─────────────────────────────────────────────────
  const sendMessage = async () => {
    const text = input.trim();
    if ((!text && !attachedFiles.length) || !chatCode || loading) return;
    const detectedLang = detectLang(text);
    const botLang = selectedBot?.ttsLanguage?.startsWith("en") ? "English"
      : selectedBot?.ttsLanguage?.startsWith("de") ? "German"
      : selectedBot?.ttsLanguage?.startsWith("fr") ? "French"
      : selectedBot?.ttsLanguage?.startsWith("es") ? "Spanish"
      : selectedBot?.ttsLanguage?.startsWith("ar") ? "Arabic"
      : selectedBot?.ttsLanguage?.startsWith("ru") ? "Russian"
      : "Turkish";
    setInput("");
    const displayText = text + (attachedFiles.length ? ` 📎 ${attachedFiles.map(f => f.name).join(", ")}` : "");
    setLoading(true);
    setMessages(prev => [...prev, { role: "user", text: displayText, _id: Date.now() }]);
    try {
      let res;
      if (attachedFiles.length) {
        const fd = new FormData();
        fd.append("message", text || "Bu dosyayı inceler misin?");
        fd.append("webSearch", webSearch ? "true" : "false");
        fd.append("tts", ttsOn ? "true" : "false");
        if (detectedLang) fd.append("userLang", detectedLang);
        attachedFiles.forEach(f => fd.append("files", f));
        res = await fetch(`/api/platform/chat/${chatCode}/send`, { method: "POST", headers: authHead(), body: fd });
        setAttachedFiles([]);
      } else {
        res = await fetch(`/api/platform/chat/${chatCode}/send`, {
          method: "POST",
          headers: { ...authHead(), "Content-Type": "application/json" },
          body: JSON.stringify({ message: text, webSearch, tts: ttsOn, userLang: detectedLang || botLang }),
        });
      }
      const data = await res.json();
      setMessages(prev => [...prev, { role: "bot", text: data.reply || "...", _id: Date.now() + 1 }]);
      emitGami(data);
      if (ttsOn && (data.audioUrl || data.audioKey)) playTtsAudio(data.audioKey, data.audioUrl);
    } catch {
      setMessages(prev => [...prev, { role: "bot", text: "Bir hata oluştu.", _id: Date.now() + 1 }]);
    } finally {
      setLoading(false);
      if (webSearch) setWebSearch(false); // web arama TEK SEFERLIK — her sorgu icin tekrar tikla (maliyet)
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  // ── Manual voice recording ────────────────────────────────────────
  const startRecording = async () => {
    if (!chatCode) { setStatus("Önce bir sohbet başlatın."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickAudioMime() || "audio/webm";
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        sendVoiceMessage(new Blob(audioChunksRef.current, { type: mimeType }), mimeType);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setStatus("Kayıt yapılıyor... Durdurmak için tekrar tıklayın.");
    } catch { setStatus("Mikrofon erişimi reddedildi. Tarayıcı izinlerini kontrol edin."); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
    setRecording(false);
    setStatus("");
  };

  const sendVoiceMessage = async (blob, mimeType) => {
    if (!chatCode) return;
    setLoading(true);
    setMessages(prev => [...prev, { role: "user", text: "🎤 Sesli mesaj gönderildi...", _id: Date.now() }]);
    const ext = mimeExt(mimeType);
    const fd = new FormData();
    fd.append("audio", blob, `voice-message${ext}`);
    fd.append("tts", "true");
    fd.append("webSearch", webSearch ? "true" : "false");
    try {
      const res = await fetch(`/api/platform/chat/${chatCode}/send`, { method: "POST", headers: authHead(), body: fd });
      const data = await res.json();
      setMessages(prev => {
        const updated = [...prev];
        const idx = updated.map(m => m.role).lastIndexOf("user");
        if (idx >= 0 && updated[idx].text.includes("🎤")) {
          updated[idx] = { ...updated[idx], text: data.userText || "🎤 Sesli mesaj" };
        }
        return updated;
      });
      setMessages(prev => [...prev, { role: "bot", text: data.reply || "...", _id: Date.now() + 1 }]);
      emitGami(data);
      if (data.audioUrl || data.audioKey) playTtsAudio(data.audioKey, data.audioUrl);
    } catch {
      setMessages(prev => [...prev, { role: "bot", text: "Ses işlenemedi. Tekrar deneyin.", _id: Date.now() + 1 }]);
    } finally {
      setLoading(false);
      if (webSearch) setWebSearch(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  // ── Live conversation (VAD) ──────────────────────────────────────
  const sendLiveVoiceMessage = async (blob, mimeType) => {
    if (!chatCode) return;
    setLoading(true);
    setMessages(prev => [...prev, { role: "user", text: "🎤 ...", _id: Date.now() }]);
    const ext = mimeExt(mimeType);
    const fd = new FormData();
    fd.append("audio", blob, `live-voice${ext}`);
    fd.append("tts", "true");
    fd.append("webSearch", webSearch ? "true" : "false");
    try {
      const res = await fetch(`/api/platform/chat/${chatCode}/send`, { method: "POST", headers: authHead(), body: fd });
      const data = await res.json();
      setMessages(prev => {
        const updated = [...prev];
        const idx = updated.map(m => m.role).lastIndexOf("user");
        if (idx >= 0 && updated[idx].text === "🎤 ...") {
          updated[idx] = { ...updated[idx], text: data.userText || "🎤 Sesli mesaj" };
        }
        return updated;
      });
      setMessages(prev => [...prev, { role: "bot", text: data.reply || "...", _id: Date.now() + 1 }]);
      emitGami(data);
      if (data.audioUrl || data.audioKey) {
        liveStateRef.current = "playing";
        setLiveState("playing");
        playTtsAudio(data.audioKey, data.audioUrl, () => {
          if (liveModeRef.current) { liveStateRef.current = "listening"; setLiveState("listening"); }
        });
      } else {
        if (liveModeRef.current) { liveStateRef.current = "listening"; setLiveState("listening"); }
      }
    } catch {
      setMessages(prev => [...prev, { role: "bot", text: "Ses işlenemedi.", _id: Date.now() + 1 }]);
      if (liveModeRef.current) { liveStateRef.current = "listening"; setLiveState("listening"); }
    } finally {
      setLoading(false);
    }
  };

  const stopLiveMode = () => {
    liveModeRef.current = false;
    liveStateRef.current = "idle";
    if (vadRafRef.current) { cancelAnimationFrame(vadRafRef.current); vadRafRef.current = null; }
    if (vadMRRef.current?.state === "recording") { try { vadMRRef.current.stop(); } catch {} }
    vadMRRef.current = null;
    vadChunksRef.current = [];
    if (vadStreamRef.current) { vadStreamRef.current.getTracks().forEach(t => t.stop()); vadStreamRef.current = null; }
    if (vadAudioCtxRef.current) { try { vadAudioCtxRef.current.close(); } catch {} vadAudioCtxRef.current = null; }
    vadAnalyserRef.current = null;
    silenceTimerRef.current = null;
    speechStartRef.current = null;
    // Stop any playing TTS
    if (audioRef.current) { try { audioRef.current.pause(); } catch {} }
    setIsSpeaking(false);
    stopVisualizer();
    setLiveMode(false);
    setLiveState("idle");
  };

  const startLiveMode = async () => {
    if (!chatCode) { setStatus("Önce bir bot seçin."); return; }
    if (recording) stopRecording();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 }
      });
      vadStreamRef.current = stream;

      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      vadAudioCtxRef.current = ctx;

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.4;
      vadAnalyserRef.current = analyser;
      ctx.createMediaStreamSource(stream).connect(analyser);
      // NOT connected to ctx.destination — no mic echo

      liveModeRef.current = true;
      liveStateRef.current = "listening";
      setLiveMode(true);
      setLiveState("listening");
      setStatus("");

      // VAD loop — closes over analyser and stream directly for stability
      const loop = () => {
        if (!liveModeRef.current) return; // live mode stopped

        const curState = liveStateRef.current;

        // Only process audio in listening/recording states
        if (curState !== "listening" && curState !== "recording") {
          vadRafRef.current = requestAnimationFrame(loop);
          return;
        }

        const buf = new Float32Array(analyser.fftSize);
        analyser.getFloatTimeDomainData(buf);
        const rms = Math.sqrt(buf.reduce((s, v) => s + v * v, 0) / buf.length);

        if (curState === "listening" && rms > VAD_SPEECH_RMS) {
          // Speech detected — start recording
          speechStartRef.current = Date.now();
          silenceTimerRef.current = null;
          liveStateRef.current = "recording";
          setLiveState("recording");

          const mime = pickAudioMime() || "audio/webm";
          vadChunksRef.current = [];
          const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
          mr.ondataavailable = (e) => { if (e.data.size > 0) vadChunksRef.current.push(e.data); };
          mr.onstop = () => {
            if (!liveModeRef.current) { vadChunksRef.current = []; return; }
            const blob = new Blob(vadChunksRef.current, { type: mime });
            vadChunksRef.current = [];
            sendLiveVoiceMessage(blob, mime);
          };
          mr.start();
          vadMRRef.current = mr;

        } else if (curState === "recording") {
          if (rms < VAD_SILENCE_RMS) {
            if (!silenceTimerRef.current) {
              silenceTimerRef.current = Date.now();
            } else if (Date.now() - silenceTimerRef.current >= VAD_SILENCE_MS) {
              const dur = Date.now() - (speechStartRef.current || 0);
              if (dur < VAD_MIN_SPEECH_MS) {
                // Too short (noise click) — discard
                if (vadMRRef.current?.state === "recording") { try { vadMRRef.current.stop(); } catch {} }
                vadChunksRef.current = [];
                vadMRRef.current = null;
                silenceTimerRef.current = null;
                speechStartRef.current = null;
                liveStateRef.current = "listening";
                setLiveState("listening");
              } else {
                // Real speech ended — stop recording (onstop sends it)
                liveStateRef.current = "processing";
                setLiveState("processing");
                if (vadMRRef.current?.state === "recording") { try { vadMRRef.current.stop(); } catch {} }
              }
            }
          } else {
            silenceTimerRef.current = null; // still speaking, reset silence timer
          }
        }

        vadRafRef.current = requestAnimationFrame(loop);
      };

      vadRafRef.current = requestAnimationFrame(loop);

    } catch {
      setStatus("Mikrofon erişimi reddedildi. Tarayıcı izinlerini kontrol edin.");
    }
  };
  // ────────────────────────────────────────────────────────────────

  const noSub = myBotIds !== null && myBotIds !== "all" && myBotIds.length === 0;

  if (noSub) return (
    <Box sx={{ display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
               height:"100%",gap:2,background:"transparent" }}>
      <SmartToyIcon sx={{ fontSize:64,color:"#4F7CFF",opacity:0.5 }} />
      <Typography variant="h6" fontWeight={600}>Henüz aktif aboneliğiniz yok</Typography>
      <Typography color="text.secondary">Bot kullanmak için bir paket seçin.</Typography>
      <Button variant="contained" startIcon={<PaymentIcon />} onClick={() => navigate("/packages")}
        sx={{ background:"linear-gradient(135deg,#4F7CFF,#8B6CFF)" }}>
        Paketlere Git
      </Button>
    </Box>
  );

  return (
    <Box sx={{ display:"flex",flexDirection:"column",height:"100%",overflow:"hidden",background:"transparent" }}>

      {/* Mesajlar alanı */}
      <Box sx={{ flexGrow:1,overflow:"auto",display:"flex",flexDirection:"column",position:"relative" }}>

        {/* Ses dalgası — BOT konuşurken (mavi-mor) / KULLANICI konuşurken (zümrüt yeşili) */}
        {(() => {
          const userTalking = liveState === "recording";
          const active = isSpeaking || userTalking;
          // Watch tarzı simetrik, ortası yüksek bar dizilimi
          const heights = [18,30,44,58,44,30,18];
          const barGrad = userTalking
            ? "linear-gradient(180deg,#6EE7B7,#10B981,#059669)"   // kullanıcı → zümrüt
            : "linear-gradient(180deg,#34D2E8,#4F7CFF,#8B6CFF)";  // bot → cyan-mavi-mor
          const glow = userTalking ? "rgba(16,185,129,0.22)" : "rgba(99,102,241,0.18)";
          return (
            <Box sx={{ position:"sticky",top:10,left:0,right:0,zIndex:5,
              display:"flex",justifyContent:"center",pointerEvents:"none",height:0,overflow:"visible" }}>
              <Box sx={{
                display:"flex",alignItems:"center",gap:"5px",height:54,padding:"12px 20px",
                borderRadius:"18px",background:"rgba(255,255,255,0.78)",backdropFilter:"blur(10px)",
                boxShadow:`0 8px 24px ${glow}`,border:"1px solid rgba(120,140,200,0.16)",
                opacity: active ? 1 : 0,
                transform: active ? "translateY(0) scale(1)" : "translateY(-10px) scale(0.9)",
                transition:"opacity 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease",
              }}>
                {heights.map((h,i)=>(
                  <Box key={i} sx={{
                    width:6,height:h,borderRadius:"6px",background:barGrad,
                    transformOrigin:"center center",
                    animation: active ? `${waveBarKf} 0.9s ease-in-out infinite` : "none",
                    animationDelay:`${Math.abs(i-3)*0.1}s`,
                  }} />
                ))}
              </Box>
            </Box>
          );
        })()}

        {/* Bot logo watermark */}
        {selectedBot && messages.length > 0 && (
          <Box sx={{ position:"sticky",top:"35%",left:0,right:0,height:0,
                     display:"flex",justifyContent:"center",
                     pointerEvents:"none",zIndex:0,overflow:"visible" }}>
            <Box component="img"
              src={selectedBot.avatar || "/biyz-brand-logo.png"}
              alt=""
              sx={{ width:180,height:180,objectFit:"contain",
                    opacity:0.07,mt:"-90px",userSelect:"none" }}
              onError={(e)=>{
                if(!e.target.dataset.fb){
                  e.target.dataset.fb="1";
                  e.target.src="/biyz-brand-logo.png";
                }
              }}
            />
          </Box>
        )}

        {/* Empty state */}
        {messages.length === 0 && !loading && selectedBot ? (
          <Box sx={{ display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                     flex:1,gap:1.5,py:4,px:3,textAlign:"center" }}>
            <Box sx={{
              width:88,height:88,borderRadius:"50%",mb:0.5,
              background: selectedBot.avatar ? "transparent" : "linear-gradient(135deg,#6c3de0 0%,#2196f3 60%,#00bcd4 100%)",
              display:"flex",alignItems:"center",justifyContent:"center",
              boxShadow:"0 6px 24px rgba(124,107,255,0.30)",
              border:"3px solid rgba(255,255,255,0.6)",
              overflow:"hidden",position:"relative",
              animation:`${breatheKf} 3.6s ease-in-out infinite`,
            }}>
              {selectedBot.avatar
                ? <img src={selectedBot.avatar} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}
                    onError={e=>{e.target.style.display="none";
                      e.target.insertAdjacentHTML("afterend",'<img src="/biyz-brand-logo.png" style="width:100%;height:100%;object-fit:contain">');}} />
                : <img src="/biyz-brand-logo.png" alt="bi" style={{width:"100%",height:"100%",objectFit:"contain"}}
                    onError={e=>{e.target.style.display="none";}} />}
            </Box>
            <Typography variant="h6" fontWeight={700} color="#1a1a2e" sx={{fontSize:20}}>
              {selectedBot.name}
            </Typography>
            {selectedBot.description && (
              <Typography variant="body2" color="#555" maxWidth={460} lineHeight={1.7}>
                {selectedBot.description}
              </Typography>
            )}
            <Typography variant="subtitle1" fontWeight={600} color="#1a1a2e" sx={{mt:1}}>
              Nasıl yardımcı olabilirim?
            </Typography>
            {status && (
              <Typography variant="caption" color={
                status.includes("hata") || status.includes("Erişim") ? "error" : "text.secondary"}>
                {status}
              </Typography>
            )}
          </Box>
        ) : messages.length === 0 && !loading ? (
          <Box sx={{ display:"flex",alignItems:"center",justifyContent:"center",flex:1 }}>
            <Typography color="text.secondary">Bir bot seçin</Typography>
          </Box>
        ) : (
          <Box sx={{ p:{ xs:1.25, sm:2 },display:"flex",flexDirection:"column",gap:1.5,width:"100%",maxWidth:860,mx:"auto",boxSizing:"border-box" }}>
            {messages.map((msg, i) => (
              <Box key={msg._id || i}
                sx={{ display:"flex",justifyContent:msg.role==="user"?"flex-end":"flex-start",gap:1 }}>
                {msg.role === "bot" && <BotAvatar bot={selectedBot} size={36} sx={{ mt:0.5 }} />}
                <Paper elevation={0} sx={{
                  maxWidth:"75%",px:2,py:1.5,borderRadius:"20px",
                  background: msg.role==="user"
                    ? "linear-gradient(135deg,#4F7CFF,#8B6CFF)"
                    : "rgba(255,255,255,0.82)",
                  backdropFilter: msg.role==="user" ? "none" : "blur(8px)",
                  border: msg.role==="user" ? "none" : "1px solid rgba(120,140,200,0.16)",
                  borderBottomLeftRadius: msg.role==="user" ? "20px" : "7px",
                  borderBottomRightRadius: msg.role==="user" ? "7px" : "20px",
                  color: msg.role==="user" ? "#fff" : "#1a1a2e",
                  boxShadow: msg.role==="user" ? "0 8px 22px rgba(99,102,241,0.28)" : "0 1px 4px rgba(0,0,0,0.05)",
                }}>
                  <Typography variant="body2" sx={{
                    whiteSpace:"pre-wrap",lineHeight:1.6,
                    color: msg.role==="user" ? "#fff" : "#1a1a2e",
                  }}>
                    {msg.text}
                  </Typography>
                </Paper>
                {msg.role === "user" && (
                  <Avatar sx={{ width:36,height:36,mt:0.5,bgcolor:"#0d47a1" }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                )}
              </Box>
            ))}
            {loading && (
              <Box sx={{ display:"flex",gap:1,alignItems:"center" }}>
                <BotAvatar bot={selectedBot} size={36} />
                <CircularProgress size={20} sx={{ color:"#4F7CFF" }} />
              </Box>
            )}
            <div ref={bottomRef} />
          </Box>
        )}
      </Box>

      {/* Dosya ön izleme */}
      {attachedFiles.length > 0 && (
        <Box sx={{ px:2,pt:1,display:"flex",flexWrap:"wrap",gap:0.5 }}>
          {attachedFiles.map((f, i) => (
            <Chip key={i} label={f.name} size="small"
              onDelete={() => removeFile(i)} sx={{ maxWidth:160,fontSize:11 }} />
          ))}
        </Box>
      )}

      {/* Manuel kayıt göstergesi */}
      {recording && (
        <Box sx={{ px:2,pt:1,display:"flex",alignItems:"center",gap:1 }}>
          <Box sx={{ width:10,height:10,borderRadius:"50%",bgcolor:"#ef4444",
            animation:`${recordPulse} 1s infinite` }} />
          <Typography variant="caption" color="#ef4444" fontWeight={600}>
            Kayıt yapılıyor — durdurmak için mikrofona tıklayın
          </Typography>
        </Box>
      )}

      {/* Canlı konuşma durum çubuğu */}
      {liveMode && (
        <Box sx={{
          px:2,py:1,display:"flex",alignItems:"center",gap:1.5,
          background: liveState === "recording" ? "rgba(239,68,68,0.06)"
            : liveState === "playing"    ? "rgba(33,150,243,0.06)"
            : liveState === "processing" ? "rgba(33,150,243,0.04)"
            : "rgba(34,197,94,0.06)",
          borderTop:"1px solid",
          borderColor: liveState === "recording" ? "rgba(239,68,68,0.22)"
            : liveState === "playing"    ? "rgba(33,150,243,0.22)"
            : liveState === "processing" ? "rgba(33,150,243,0.15)"
            : "rgba(34,197,94,0.22)",
          transition:"background 0.3s, border-color 0.3s",
        }}>
          {/* Listening: yeşil pulsing dot */}
          {liveState === "listening" && (
            <Box sx={{ width:10,height:10,borderRadius:"50%",bgcolor:"#22c55e",flexShrink:0,
              animation:`${livePulseGreen} 1.5s ease-in-out infinite` }} />
          )}
          {/* Recording: kırmızı nokta (ses dalgası ÜSTTEKİ ekolayzerde gösteriliyor) */}
          {liveState === "recording" && (
            <Box sx={{ width:10,height:10,borderRadius:"50%",bgcolor:"#ef4444",flexShrink:0,
              animation:`${recordPulse} 1s infinite` }} />
          )}
          {/* Processing: spinner */}
          {liveState === "processing" && (
            <CircularProgress size={14} sx={{ color:"#4F7CFF",flexShrink:0 }} />
          )}
          {/* Playing: mavi nokta (ses dalgası ÜSTTEKİ ekolayzerde gösteriliyor) */}
          {liveState === "playing" && (
            <Box sx={{ width:10,height:10,borderRadius:"50%",bgcolor:"#2196f3",flexShrink:0,
              animation:`${recordPulse} 1s infinite` }} />
          )}

          <Typography variant="caption" fontWeight={700} sx={{
            color: liveState === "recording" ? "#dc2626"
              : liveState === "playing"    ? "#1565c0"
              : liveState === "processing" ? "#1565c0"
              : "#15803d",
          }}>
            {liveState === "listening"  ? "Dinleniyor..."
              : liveState === "recording"  ? "Konuşuyorsunuz..."
              : liveState === "processing" ? "Düşünüyor..."
              : "Yanıtlıyor..."}
          </Typography>

          <Box sx={{ flexGrow:1 }} />

          {/* Eğitmenin konuşma hızı — anında uygulanır */}
          <Box sx={{ display:"flex", gap:0.5, alignItems:"center", mr:1 }}>
            <Typography variant="caption" sx={{ color:"#5B6B8C", fontWeight:600, display:{ xs:"none", md:"block" }, whiteSpace:"nowrap" }}>
              Eğitmenin Konuşma Hızı:
            </Typography>
            {[["Yavaş",0.8],["Normal",1.0],["Hızlı",1.3]].map(([lbl,val])=>(
              <Box key={val} component="button"
                onClick={()=>{ setSpeechSpeed(val); speechSpeedRef.current=val;
                  if(audioRef.current){ try{ audioRef.current.preservesPitch=true; audioRef.current.playbackRate=val; }catch{} } }}
                sx={{ border:"1px solid",
                  borderColor: speechSpeed===val ? "transparent" : "rgba(120,140,200,0.3)",
                  background: speechSpeed===val ? "linear-gradient(135deg,#4F7CFF,#8B6CFF)" : "rgba(255,255,255,0.7)",
                  color: speechSpeed===val ? "#fff" : "#5B6B8C",
                  borderRadius:"14px", px:1, py:0.25, fontSize:11, fontWeight:700,
                  cursor:"pointer", whiteSpace:"nowrap", lineHeight:1.6 }}>
                {lbl}
              </Box>
            ))}
          </Box>

          <Button size="small" variant="outlined" color="error"
            startIcon={<CallEndIcon sx={{ fontSize:13 }} />}
            onClick={stopLiveMode}
            sx={{ borderRadius:20,fontSize:11,py:0.25,px:1.5,lineHeight:1.8 }}>
            Bitir
          </Button>
        </Box>
      )}

      {/* Input alanı */}
      <Box sx={{ px:{ xs:1.25, sm:2 },pb:"calc(16px + env(safe-area-inset-bottom))",pt:"8px",
                 flexShrink:0,width:"100%",maxWidth:860,mx:"auto",boxSizing:"border-box" }}>
        <Box sx={{
          display:"flex",alignItems:"center",gap:1,
          background:"rgba(255,255,255,0.82)",backdropFilter:"blur(12px)",borderRadius:"24px",padding:"7px 10px",
          boxShadow:"0 12px 36px rgba(99,102,241,0.16)",
          border: recording  ? "1.5px solid #ef4444"
            : liveMode ? "1.5px solid #22c55e"
            : "1px solid rgba(120,140,200,0.18)",
          transition:"border-color 0.2s",
        }}>
          {/* Web arama */}
          <Box component="button" onClick={() => setWebSearch(!webSearch)} sx={{
            border: webSearch ? "1px solid transparent" : "1px solid rgba(120,140,200,0.25)",
            borderRadius:"20px",padding:"5px 15px",fontSize:13,fontWeight:600,
            color: webSearch ? "#fff" : "#5B6B8C",
            background: webSearch ? "linear-gradient(135deg,#4F7CFF,#8B6CFF)" : "rgba(255,255,255,0.6)",
            cursor:"pointer",flexShrink:0,whiteSpace:"nowrap",
          }}>
            Web
          </Box>

          {/* Dosya ekle */}
          <input ref={fileInputRef} type="file" multiple accept="*/*"
            style={{ display:"none" }} onChange={handleFileSelect} />
          <Tooltip title="Dosya ekle">
            <IconButton size="small" onClick={() => fileInputRef.current?.click()}
              sx={{ color: attachedFiles.length ? "#4F7CFF" : "#999",flexShrink:0 }}>
              <AttachFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Text input */}
          <TextField fullWidth multiline maxRows={4} variant="standard"
            placeholder={recording ? "Konuşun..." : liveMode ? "veya yazın..." : "Herhangi bir şey sor"}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            inputRef={inputRef}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            autoFocus
            InputProps={{ disableUnderline: true }}
            sx={{ "& textarea":{ fontSize:14,color:"#333","&::placeholder":{ color:"#aaa" } } }}
          />

          {/* TTS toggle */}
          <Tooltip title={ttsOn ? "Bot sesi Açık — kapat" : "Bot sesi Kapalı — aç"}>
            <IconButton size="small" onClick={() => setTtsOn(!ttsOn)}
              sx={{ color: ttsOn ? "#4F7CFF" : "#bbb",flexShrink:0 }}>
              {ttsOn ? <VolumeUpIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          {/* Manuel mikrofon (canlı moddayken devre dışı) */}
          <Tooltip title={recording ? "Kaydı durdur ve gönder" : "Sesli mesaj gönder"}>
            <span>
              <IconButton size="small"
                onClick={recording ? stopRecording : startRecording}
                disabled={loading || liveMode}
                sx={{
                  color: recording ? "#ef4444" : "#888",flexShrink:0,
                  animation: recording ? `${recordPulse} 1s infinite` : "none",
                  bgcolor: recording ? "rgba(239,68,68,0.08)" : "transparent",
                  borderRadius:"50%",
                }}>
                {recording ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>

          {/* Canlı konuşma butonu */}
          <Tooltip title={liveMode ? "Canlı konuşmayı bitir" : "Canlı konuşma başlat"}>
            <span>
              <IconButton size="small"
                onClick={liveMode ? stopLiveMode : startLiveMode}
                disabled={loading || recording || !chatCode}
                sx={{
                  color: liveMode ? "#22c55e" : "#888",flexShrink:0,
                  animation: liveMode && liveState === "listening" ? `${livePulseGreen} 1.5s ease-in-out infinite` : "none",
                  bgcolor: liveMode ? "rgba(34,197,94,0.10)" : "transparent",
                  borderRadius:"50%",
                }}>
                <RecordVoiceOverIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          {/* Gönder */}
          <IconButton onClick={sendMessage}
            disabled={loading || (!input.trim() && !attachedFiles.length)}
            sx={{ flexShrink:0, width:40, height:40,
              background: (input.trim() || attachedFiles.length) ? "linear-gradient(135deg,#4F7CFF,#8B6CFF)" : "rgba(120,140,200,0.15)",
              color: (input.trim() || attachedFiles.length) ? "#fff" : "#bbb",
              "&:hover": { background: (input.trim() || attachedFiles.length) ? "linear-gradient(135deg,#3B6BFF,#7A5BFF)" : "rgba(120,140,200,0.15)" },
              "&.Mui-disabled": { background: "rgba(120,140,200,0.15)", color:"#ccc" } }}>
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
